import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  AliasClientMessage,
  AliasErrorCode,
  AliasPrivateState,
  AliasPublicState,
  AliasServerMessage,
} from '@shum/shared';
import { websocketUrl } from './runtime';
import { useConnectionWatchdog } from './connectionWatchdog';

export type AliasSocketStatus = 'connecting' | 'open' | 'closed';

export interface AliasSocketState {
  status: AliasSocketStatus;
  publicState: AliasPublicState | null;
  privateState: AliasPrivateState | null;
  session: { playerId: string; sessionToken: string; recoveryCode: string } | null;
  error: { code: AliasErrorCode; message: string } | null;
  replaced: boolean;
}

const INITIAL: AliasSocketState = {
  status: 'connecting',
  publicState: null,
  privateState: null,
  session: null,
  error: null,
  replaced: false,
};

export function aliasActionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID().replace(/-/g, '');
  return `alias_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

export function useAliasSocket(code: string | undefined, options: { publicOnly?: boolean } = {}) {
  const [state, setState] = useState<AliasSocketState>(INITIAL);
  const socketRef = useRef<WebSocket | null>(null);
  const stoppedRef = useRef(false);
  const attemptRef = useRef(0);
  const connectRef = useRef<(() => void) | null>(null);
  const reconnectTimerRef = useRef<number | undefined>(undefined);
  const lastMessageAtRef = useRef<number>(Date.now());
  const awaitingSnapshotRef = useRef(false);
  const publicOnly = options.publicOnly === true;

  const send = useCallback((message: AliasClientMessage): boolean => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify(message));
    return true;
  }, []);

  useEffect(() => {
    if (!code) return;
    const roomCode = code.toUpperCase();
    let alive = true;
    stoppedRef.current = false;
    setState((previous) => ({ ...INITIAL, session: previous.session }));

    const connect = () => {
      const current = socketRef.current;
      if (current && (current.readyState === WebSocket.CONNECTING || current.readyState === WebSocket.OPEN)) return;

      const saved = publicOnly ? null : readSession(roomCode);
      awaitingSnapshotRef.current = Boolean(saved?.sessionToken);
      const socket = new WebSocket(websocketUrl(`/ws/alias/${roomCode}`));
      socketRef.current = socket;
      lastMessageAtRef.current = Date.now();
      setState((previous) => ({ ...previous, status: 'connecting' }));
      socket.onopen = () => {
        if (!alive || socketRef.current !== socket) return;
        attemptRef.current = 0;
        lastMessageAtRef.current = Date.now();
        setState((previous) => ({ ...previous, status: 'connecting', error: null }));
        if (saved?.sessionToken) socket.send(JSON.stringify({ type: 'alias.reconnect', sessionToken: saved.sessionToken } satisfies AliasClientMessage));
      };
      socket.onmessage = (event) => {
        if (!alive || socketRef.current !== socket) return;
        lastMessageAtRef.current = Date.now();
        try {
          const message = JSON.parse(String(event.data)) as AliasServerMessage;
          setState((previous) => {
            const next = applyMessage(previous, message, roomCode);
            const synchronized =
              message.type === 'alias.private_state' ||
              (message.type === 'alias.public_state' && !awaitingSnapshotRef.current) ||
              (message.type === 'alias.error' && awaitingSnapshotRef.current);
            if (!synchronized) return next;
            awaitingSnapshotRef.current = false;
            return { ...next, status: 'open' };
          });
        } catch {
          // Ignore malformed network payloads; the next public state will resync the UI.
        }
      };
      socket.onclose = () => {
        if (!alive || stoppedRef.current || socketRef.current !== socket) return;
        socketRef.current = null;
        setState((previous) => ({ ...previous, status: 'closed' }));
        attemptRef.current += 1;
        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = undefined;
          connect();
        }, Math.min(15_000, 800 * 2 ** Math.min(attemptRef.current, 4)));
      };
    };
    connectRef.current = connect;
    const initialTimer = window.setTimeout(connect, 0);
    return () => {
      alive = false;
      stoppedRef.current = true;
      connectRef.current = null;
      window.clearTimeout(initialTimer);
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [code, publicOnly]);

  // Force a fresh connection with the same parameters (manual button / watchdog).
  const reconnect = useCallback(() => {
    if (stoppedRef.current || !connectRef.current) return;
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = undefined;
    }
    const socket = socketRef.current;
    if (socket) {
      socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null;
      try {
        socket.close();
      } catch {
        // Already closing/closed; a new socket replaces it below.
      }
    }
    socketRef.current = null;
    attemptRef.current = 0;
    awaitingSnapshotRef.current = true;
    setState((previous) => ({ ...previous, status: 'connecting' }));
    connectRef.current();
  }, []);

  useConnectionWatchdog({ wsRef: socketRef, stoppedRef, lastMessageAtRef, reconnect });

  const selectPlayer = useCallback((playerId: string) => {
    const ok = send({ type: 'alias.player.select', playerId, actionId: aliasActionId() });
    if (ok) {
      awaitingSnapshotRef.current = true;
      setState((previous) => ({ ...previous, status: 'connecting' }));
    }
    return ok;
  }, [send]);
  const releaseIdentity = useCallback(() => send({ type: 'alias.identity.release', actionId: aliasActionId() }), [send]);
  const recover = useCallback((recoveryCode: string) => {
    const ok = send({ type: 'alias.reconnect', recoveryCode });
    if (ok) {
      awaitingSnapshotRef.current = true;
      setState((previous) => ({ ...previous, status: 'connecting' }));
    }
    return ok;
  }, [send]);
  const clearError = useCallback(() => setState((previous) => ({ ...previous, error: null })), []);

  return useMemo(() => ({ state, send, selectPlayer, releaseIdentity, recover, clearError, reconnect }), [state, send, selectPlayer, releaseIdentity, recover, clearError, reconnect]);
}

function applyMessage(previous: AliasSocketState, message: AliasServerMessage, code: string): AliasSocketState {
  switch (message.type) {
    case 'alias.public_state': return { ...previous, publicState: message.state };
    case 'alias.private_state': return { ...previous, privateState: message.state, replaced: false };
    case 'alias.session': {
      const session = { playerId: message.playerId, sessionToken: message.sessionToken, recoveryCode: message.recoveryCode };
      safeSet(sessionKey(code), JSON.stringify(session));
      return { ...previous, session, replaced: false };
    }
    case 'alias.identity.released':
      safeRemove(sessionKey(code));
      return { ...previous, session: null, privateState: null, replaced: false };
    case 'alias.reconnected': return { ...previous, replaced: false };
    case 'alias.error': return { ...previous, error: { code: message.code, message: message.message } };
    case 'session_replaced': return { ...previous, replaced: true, privateState: null };
    case 'hello':
    case 'pong':
      return previous;
  }
}

function readSession(code: string): { playerId: string; sessionToken: string; recoveryCode: string } | null {
  const raw = safeGet(sessionKey(code));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { playerId?: string; sessionToken?: string; recoveryCode?: string };
    if (parsed.playerId && parsed.sessionToken && parsed.recoveryCode) return { playerId: parsed.playerId, sessionToken: parsed.sessionToken, recoveryCode: parsed.recoveryCode };
  } catch {
    // A corrupted local value should never prevent joining the room again.
  }
  return null;
}

function sessionKey(code: string): string {
  const deviceId = getDeviceSessionId();
  return deviceId ? `alias-session:${code.toUpperCase()}:${deviceId}` : `alias-session:${code.toUpperCase()}`;
}

function getDeviceSessionId(): string | null {
  try {
    const id = new URLSearchParams(window.location.search).get('deviceSessionId');
    return id && /^[A-Za-z0-9_-]{4,80}$/.test(id) ? id : null;
  } catch {
    return null;
  }
}

function safeGet(key: string): string | null { try { return localStorage.getItem(key); } catch { return null; } }
function safeSet(key: string, value: string): void { try { localStorage.setItem(key, value); } catch { /* recovery code remains usable */ } }
function safeRemove(key: string): void { try { localStorage.removeItem(key); } catch { /* no-op */ } }
